import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  signal,
  SimpleChanges,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TranslatePipe } from '@ngx-translate/core';

import { ProductService } from '../../../core/services/product.service';
import { ProductDto } from '../../../core/models/product.models';
import { ProductVariantDto, ProductVariantMatrixDto } from '../../../core/models/variant.models';
import { MoneyPipe } from '../../pipes/money.pipe';

export interface CartAddEvent {
  productId: string;
  productName: string;
  unitPrice: number;
  quantity: number;
  discount: number;
  stocks: number;
  imageUrl: string | null;
  productVariantId: string | null;
  variantLabel: string | null;
  /** Mahsulotning sotuv valyutasi — savat va order shu valyutada bo'ladi. */
  currency: string;
}

@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TagModule,
    ButtonModule,
    DialogModule,
    TranslatePipe,
  ],
  templateUrl: './product-detail.component.html',
})
export class ProductDetailComponent implements OnChanges {
  private productSvc = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() product: ProductDto | null = null;
  @Input() storeId: string | null = null;
  /** 'view' — shows Edit button; 'sale' — shows qty input + Add to cart. */
  @Input() mode: 'view' | 'sale' = 'view';
  @Input() storeName: string | null = null;

  @Output() addToCart = new EventEmitter<CartAddEvent>();
  @Output() requestEdit = new EventEmitter<ProductDto>();

  loading = signal(false);
  imageZoom = false;
  matrix = signal<ProductVariantMatrixDto | null>(null);
  selectedValues = signal<Record<string, string>>({});
  quantity = signal(1);

  hasVariants = computed(() => {
    const m = this.matrix();
    return !!m && m.attributes.length > 0 && m.variants.length > 0;
  });

  selectedVariant = computed<ProductVariantDto | null>(() => {
    const m = this.matrix();
    const sel = this.selectedValues();
    if (!m || m.variants.length === 0 || m.attributes.length === 0) return null;
    for (const ax of m.attributes) {
      if (!sel[ax.attributeId]) return null;
    }
    return (
      m.variants.find((v) =>
        m.attributes.every(
          (ax) =>
            v.values.find((val) => val.attributeId === ax.attributeId)?.valueId ===
            sel[ax.attributeId],
        ),
      ) ?? null
    );
  });

  effectivePrice = computed(
    () => this.selectedVariant()?.effectiveSellPrice ?? this.product?.sellPrice ?? 0,
  );

  /**
   * Sotuvga yaroqli qoldiq. Variantli mahsulotda — TANLANGAN variant qoldig'i
   * (tanlanmaguncha 0). Oddiy mahsulotda — matritsadagi default variant qoldig'i
   * (storeId filtriga mos: do'kon tanlansa o'sha do'kon, aks holda umumiy).
   * Matritsa hali yuklanmagan bo'lsa product.stocks'ga tayanamiz.
   */
  effectiveStock = computed(() => {
    if (this.hasVariants()) {
      const v = this.selectedVariant();
      // Variant tanlangan — o'sha variant qoldig'i; tanlanmagan — barcha
      // variantlar yig'indisi (umumiy mavjud qoldiq).
      if (v) return v.stock ?? 0;
      return this.matrix()!.variants.reduce((sum, x) => sum + (x.stock ?? 0), 0);
    }
    const m = this.matrix();
    if (m && m.variants.length > 0) {
      // Variantsiz mahsulotda matritsada bitta default variant bo'ladi — uning
      // qoldig'i storeId filtriga mos hisoblangan (umumiy yoki do'kon bo'yicha).
      return m.variants.reduce((sum, v) => sum + (v.stock ?? 0), 0);
    }
    return this.product?.stocks ?? 0;
  });

  /** Miqdor kiritish mumkinmi: variantsiz mahsulot YOKI variant tanlangan bo'lsa. */
  canPickQuantity = computed(() => !this.hasVariants() || !!this.selectedVariant());

  canAddToCart = computed(
    () => this.mode === 'sale' && this.canPickQuantity() && this.effectiveStock() > 0,
  );

  constructor() {
    // Variant almashganda (yoki qoldiq kamayganda) miqdorni mavjud qoldiqqa moslaymiz.
    effect(() => {
      const max = this.effectiveStock();
      untracked(() => {
        const q = this.quantity();
        if (q > max) this.quantity.set(Math.max(1, max));
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product'] || changes['storeId']) {
      const p = this.product;
      if (p) {
        this.loadMatrix(p);
      } else {
        this.matrix.set(null);
        this.selectedValues.set({});
        this.quantity.set(1);
      }
    }
  }

  private loadMatrix(product: ProductDto): void {
    this.matrix.set(null);
    this.selectedValues.set({});
    this.quantity.set(1);
    this.loading.set(true);
    this.productSvc
      .getVariantMatrix(product.id, this.storeId ?? undefined)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (res) => {
          const m = res.data ?? null;
          this.matrix.set(m);
          if (m) {
            const init: Record<string, string> = {};
            for (const ax of m.attributes) {
              if (ax.values.length === 1) init[ax.attributeId] = ax.values[0].valueId;
            }
            this.selectedValues.set(init);
          }
        },
        error: () => this.matrix.set(null),
      });
  }

  selectVariantValue(attributeId: string, valueId: string): void {
    this.selectedValues.update((sel) => ({
      ...sel,
      [attributeId]: sel[attributeId] === valueId ? '' : valueId,
    }));
  }

  isVariantValueSelected(attributeId: string, valueId: string): boolean {
    return this.selectedValues()[attributeId] === valueId;
  }

  isValueAvailable(attributeId: string, valueId: string): boolean {
    const m = this.matrix();
    if (!m) return true;
    const sel = this.selectedValues();
    return m.variants.some((v) => {
      const hasValue = v.values.some(
        (val) => val.attributeId === attributeId && val.valueId === valueId,
      );
      if (!hasValue) return false;
      const consistent = m.attributes.every((ax) => {
        if (ax.attributeId === attributeId) return true;
        const s = sel[ax.attributeId];
        if (!s) return true;
        return v.values.find((val) => val.attributeId === ax.attributeId)?.valueId === s;
      });
      return consistent && (v.stock ?? 0) > 0;
    });
  }

  increaseQty(): void {
    if (this.quantity() < this.effectiveStock()) this.quantity.update((q) => q + 1);
  }

  decreaseQty(): void {
    if (this.quantity() > 1) this.quantity.update((q) => q - 1);
  }

  variantLabel(v: ProductVariantDto): string {
    return v.values.map((vv) => vv.value).join(' • ');
  }

  copyBarcode(barcode: string): void {
    void navigator.clipboard.writeText(barcode);
  }

  onAddToCart(): void {
    const product = this.product;
    if (!product || !this.canAddToCart()) return;
    const variant = this.selectedVariant();
    const label = variant ? variant.values.map((v) => v.value).join(' / ') : null;

    this.addToCart.emit({
      productId: product.id,
      productName: product.name,
      unitPrice: this.effectivePrice(),
      quantity: Math.min(this.quantity(), this.effectiveStock()),
      discount: product.discount ?? 0,
      stocks: this.effectiveStock(),
      imageUrl: product.imageUrl,
      productVariantId: variant?.id ?? null,
      variantLabel: label,
      currency: product.sellCurrency,
    });
  }
}
